import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  CommandAuditEntry,
  CommandEvent,
  CommandItem,
  ExecuteCommandInput,
  NotRightReason,
} from "@ceo-cmd/shared";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { MobileSheet } from "@/components/mobile-sheet";
import { cn } from "@/cn";
import { getTodayItemAudit, getTodayItemEvents } from "@/api/today";
import {
  askAiHref,
  buildDelegateInput,
  laterRemindAt,
  NOT_RIGHT_REASONS,
  personLine,
  RETURN_CONDITIONS,
  type ReturnCondition,
} from "./action-helpers";
import { DecisionHistory } from "./decision-history";
import { dueLabel, formatMoney, priorityClass } from "./labels";

type SheetKind =
  | "review"
  | "more"
  | "delegate"
  | "later"
  | "not_right"
  | "confirm"
  | null;

const SWIPE_THRESHOLD_PX = 56;

export function MobileFocusCard({
  item,
  moveIndex,
  moveTotal,
  busy,
  onExecute,
  onPrev,
  onNext,
}: {
  item: CommandItem;
  moveIndex: number;
  moveTotal: number;
  busy?: boolean;
  onExecute: (body: ExecuteCommandInput) => void;
  onPrev?: () => void;
  onNext?: () => void;
  /** @deprecated */
  completedToday?: number;
  /** @deprecated */
  showCompany?: boolean;
}) {
  const [sheet, setSheet] = useState<SheetKind>(null);
  const [assignee, setAssignee] = useState("Omar");
  const [instruction, setInstruction] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [returnCondition, setReturnCondition] =
    useState<ReturnCondition>("When completed");
  const [customDate, setCustomDate] = useState("");
  const [events, setEvents] = useState<CommandEvent[]>([]);
  const [audit, setAudit] = useState<CommandAuditEntry[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swipeLocked = useRef(false);

  useEffect(() => {
    setEvents([]);
    setAudit([]);
    setHistoryLoaded(false);
  }, [item.id]);

  useEffect(() => {
    if (sheet !== "review" || historyLoaded) return;
    void Promise.all([
      getTodayItemEvents(item.id).catch(() => ({ events: [] as CommandEvent[] })),
      getTodayItemAudit(item.id).catch(() => ({
        audit: [] as CommandAuditEntry[],
      })),
    ]).then(([eventsRes, auditRes]) => {
      setEvents(eventsRes.events);
      setAudit(auditRes.audit);
      setHistoryLoaded(true);
    });
  }, [sheet, item.id, historyLoaded]);

  const person = personLine(item);
  const canPrev = Boolean(onPrev) && moveIndex > 1;
  const canNext = Boolean(onNext) && moveIndex < moveTotal;
  const progress =
    moveTotal > 0 ? Math.min(1, moveIndex / moveTotal) : 0;
  const progressLabel =
    moveTotal === 0
      ? "All clear for today"
      : `Move ${moveIndex} of ${moveTotal}`;

  function onTouchStart(e: React.TouchEvent) {
    if (sheet || moveTotal <= 1) return;
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    swipeLocked.current = false;
  }

  function onTouchMove(e: React.TouchEvent) {
    if (!touchStart.current || swipeLocked.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 24) {
      touchStart.current = null;
      return;
    }
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    swipeLocked.current = true;
    if (dx < 0 && canNext) onNext?.();
    if (dx > 0 && canPrev) onPrev?.();
    touchStart.current = null;
  }

  function onTouchEnd() {
    touchStart.current = null;
    swipeLocked.current = false;
  }

  function runPrimary() {
    if (
      item.actionDefinition.confirmationRequired ||
      item.type === "ACKNOWLEDGE"
    ) {
      setSheet("confirm");
      return;
    }
    onExecute({ disposition: "execute" });
  }

  function handleNotRight(reason: NotRightReason) {
    if (reason === "wrong_timing") {
      setSheet("later");
      return;
    }
    if (reason === "wrong_owner") {
      setSheet("delegate");
      return;
    }
    onExecute({ disposition: "not_right", notRightReason: reason });
    setSheet(null);
  }

  return (
    <div
      className="flex h-full flex-col"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div className="mb-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            {moveTotal > 1 && (
              <button
                type="button"
                aria-label="Previous move"
                disabled={!canPrev || busy}
                onClick={() => onPrev?.()}
                className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-border text-foreground disabled:opacity-30"
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            <p className="truncate text-sm font-medium text-muted-foreground">
              {progressLabel}
            </p>
            {moveTotal > 1 && (
              <button
                type="button"
                aria-label="Next move"
                disabled={!canNext || busy}
                onClick={() => onNext?.()}
                className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-border text-foreground disabled:opacity-30"
              >
                <ChevronRight className="size-4" />
              </button>
            )}
          </div>
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
              priorityClass(item.priority),
            )}
          >
            {item.priority}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted/50">
          <div
            className="h-full rounded-full bg-brand transition-all"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      </div>

      <div
        key={item.id}
        className="rounded-2xl border border-border bg-card p-5 shadow-sm"
      >
        <p className="text-2xl font-semibold leading-tight text-foreground">
          {item.recommendation}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          {item.companyName}
          {person ? ` · ${person}` : ""}
        </p>
        <p className="mt-3 text-sm text-foreground">{item.whyInToday}</p>
        <p className="mt-2 text-sm text-foreground">
          <span className="font-medium">Expected:</span> {item.expectedOutcome}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">If waiting:</span>{" "}
          {item.riskOfInaction}
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
          {item.financialExposure != null && (
            <Chip>{formatMoney(item.financialExposure, item.currency)}</Chip>
          )}
          <Chip>{dueLabel(item.dueAt ?? item.reviewAt)}</Chip>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-1">
        <Secondary
          label="Review"
          disabled={busy}
          onClick={() => setSheet("review")}
        />
        <Secondary
          label="Later"
          disabled={busy}
          onClick={() => setSheet("later")}
        />
        <Secondary
          label="•••"
          ariaLabel="More actions for this move"
          disabled={busy}
          onClick={() => setSheet("more")}
        />
      </div>

      <div className="mt-auto space-y-2 pt-4 pb-[env(safe-area-inset-bottom)]">
        <button
          type="button"
          disabled={busy}
          onClick={runPrimary}
          className="h-12 w-full rounded-xl bg-brand text-base font-semibold text-brand-foreground disabled:opacity-50"
        >
          {item.actionDefinition.actionLabel}
        </button>
      </div>

      <MobileSheet
        open={sheet === "review"}
        onClose={() => setSheet(null)}
        title="Review"
        variant="bottom"
      >
        <div className="space-y-3 px-4 py-3 text-sm">
          <p className="font-medium text-foreground">{item.recommendation}</p>
          <p className="text-muted-foreground">{item.situationSummary}</p>
          <p>
            <span className="font-medium">Expected:</span>{" "}
            {item.expectedOutcome}
          </p>
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">Risk of waiting:</span>{" "}
            {item.riskOfInaction}
          </p>
          {item.alternatives.length > 0 && (
            <ul className="list-inside list-disc text-muted-foreground">
              {item.alternatives.map((a) => (
                <li key={a.id}>{a.label}</li>
              ))}
            </ul>
          )}
          {item.evidence.slice(0, 4).map((s) => (
            <div
              key={s.id}
              className="rounded border border-border px-2 py-1.5 text-xs"
            >
              {s.label}
            </div>
          ))}
          {historyLoaded && (
            <DecisionHistory events={events} auditFallback={audit} />
          )}
          {item.sourceLinks.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {item.sourceLinks.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded border border-border px-2 py-1 text-xs"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </MobileSheet>

      <MobileSheet
        open={sheet === "more"}
        onClose={() => setSheet(null)}
        title="More"
        variant="bottom"
      >
        <div className="space-y-2 px-4 py-3">
          <SheetButton onClick={() => setSheet("delegate")}>
            Delegate
          </SheetButton>
          <Link
            to={askAiHref(item)}
            className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-3 text-left text-sm"
            onClick={() => setSheet(null)}
          >
            <Sparkles className="size-4" />
            Ask AI
          </Link>
          <SheetButton onClick={() => setSheet("not_right")}>
            Not right
          </SheetButton>
        </div>
      </MobileSheet>

      <MobileSheet
        open={sheet === "delegate"}
        onClose={() => setSheet(null)}
        title="Delegate"
        variant="bottom"
      >
        <div className="space-y-2 px-4 py-3">
          <input
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            placeholder="Person"
          />
          <textarea
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
            rows={2}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Short instruction"
          />
          <input
            type="date"
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <select
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
            value={returnCondition}
            onChange={(e) =>
              setReturnCondition(e.target.value as ReturnCondition)
            }
          >
            {RETURN_CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="w-full rounded-md bg-brand px-3 py-3 text-sm font-medium text-brand-foreground"
            onClick={() => {
              onExecute(
                buildDelegateInput({
                  assignee,
                  instruction,
                  dueDate,
                  returnCondition,
                }),
              );
              setSheet(null);
            }}
          >
            Confirm delegate
          </button>
        </div>
      </MobileSheet>

      <MobileSheet
        open={sheet === "later"}
        onClose={() => setSheet(null)}
        title="Later"
        variant="bottom"
      >
        <div className="space-y-2 px-4 py-3">
          {(
            [
              ["today", "Later today"],
              ["tomorrow", "Tomorrow"],
              ["next_business_day", "Next business day"],
            ] as const
          ).map(([key, label]) => (
            <SheetButton
              key={key}
              onClick={() => {
                onExecute({
                  disposition: "later",
                  remindAt: laterRemindAt(key),
                });
                setSheet(null);
              }}
            >
              {label}
            </SheetButton>
          ))}
          <div className="flex gap-2">
            <input
              type="date"
              className="flex-1 rounded-md border border-border px-3 py-2 text-sm"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
            />
            <button
              type="button"
              disabled={!customDate}
              className="rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50"
              onClick={() => {
                onExecute({
                  disposition: "later",
                  remindAt: laterRemindAt("date", customDate),
                });
                setSheet(null);
              }}
            >
              Set
            </button>
          </div>
        </div>
      </MobileSheet>

      <MobileSheet
        open={sheet === "not_right"}
        onClose={() => setSheet(null)}
        title="Not right"
        variant="bottom"
      >
        <div className="space-y-2 px-4 py-3">
          {NOT_RIGHT_REASONS.map((reason) => (
            <SheetButton
              key={reason.id}
              onClick={() => handleNotRight(reason.id)}
            >
              {reason.label}
            </SheetButton>
          ))}
        </div>
      </MobileSheet>

      <MobileSheet
        open={sheet === "confirm"}
        onClose={() => setSheet(null)}
        title="Confirm"
        variant="bottom"
      >
        <div className="space-y-3 px-4 py-3 text-sm">
          <p className="font-medium">{item.actionDefinition.actionLabel}?</p>
          {item.type === "ACKNOWLEDGE" ? (
            <AcknowledgementDetails item={item} />
          ) : (
            <p className="text-muted-foreground">
              Updates {item.companyName} in CEO Command. External changes are
              simulated.
            </p>
          )}
          <ul className="list-inside list-disc text-xs text-muted-foreground">
            <li>Local decision recorded in CEO Command</li>
            {item.actionDefinition.externalSystemWrites.map((w) => (
              <li key={`${w.system}-${w.description}`}>
                {w.description}: simulated
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 rounded-md border border-border py-3"
              onClick={() => setSheet(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              className="flex-1 rounded-md bg-brand py-3 font-medium text-brand-foreground disabled:opacity-50"
              onClick={() => {
                setSheet(null);
                onExecute({ disposition: "execute" });
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      </MobileSheet>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-border px-2 py-0.5">
      {children}
    </span>
  );
}

function Secondary({
  label,
  ariaLabel,
  onClick,
  disabled,
}: {
  label: string;
  ariaLabel?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className="h-11 rounded-xl border border-border text-sm font-medium disabled:opacity-50"
    >
      {label}
    </button>
  );
}

function AcknowledgementDetails({ item }: { item: CommandItem }) {
  const owner = item.ownerName ?? "Group Treasury";
  const escalationAt =
    item.actionDefinition.nextReviewDate ??
    new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
  const escalationLabel = new Date(escalationAt).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-border p-3 text-xs">
        <dt className="text-muted-foreground">Finance owner</dt>
        <dd className="font-medium text-foreground">{owner}</dd>
        <dt className="text-muted-foreground">Escalate on</dt>
        <dd className="font-medium text-foreground">{escalationLabel}</dd>
        <dt className="text-muted-foreground">Return to CEO if</dt>
        <dd className="font-medium text-foreground">
          Unresolved by escalation date
        </dd>
      </dl>
      <p className="text-muted-foreground">
        This will record your acknowledgement, assign continued ownership to{" "}
        {owner}, move the item to Waiting, and return it to Today on{" "}
        {escalationLabel} if unresolved.
      </p>
    </>
  );
}

function SheetButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="w-full rounded-md border border-border px-3 py-3 text-left text-sm"
      onClick={onClick}
    >
      {children}
    </button>
  );
}
